import rawTenantConfig from "~active-tenant-config";
import { DEFAULT_FEATURES, type FeatureFlags } from "~/config/features";
import type { Branding } from "~/types/branding";

/** Deployment tier, from NEXT_PUBLIC_ENV. Only used for per-tier overrides. */
type EnvName = "demo" | "staging" | "preprod" | "production";

/** The fields a tenant may set at the top level or override per environment. */
interface TenantOverrides {
  features?: Partial<FeatureFlags>;
}

/** Shape of a tenant's config.json on disk. `environments` is optional. */
export interface RawTenantConfig extends TenantOverrides {
  /** Lowercase; the `saas/` dirs keep their mixed-case names. */
  tenant: string;
  title: string;
  branding: Branding;
  environments?: Partial<Record<EnvName, TenantOverrides>>;
}

/** Fully resolved -- every field present, so call sites have nothing to guard. */
export interface TenantConfig {
  tenant: string;
  title: string;
  branding: Branding;
  features: FeatureFlags;
}

const override =
  rawTenantConfig.environments?.[process.env.NEXT_PUBLIC_ENV as EnvName];

const tenantConfig: TenantConfig = {
  tenant: rawTenantConfig.tenant,
  title: rawTenantConfig.title,
  branding: rawTenantConfig.branding,
  features: {
    ...DEFAULT_FEATURES,
    ...rawTenantConfig.features,
    ...override?.features,
  },
};

export function getTenantConfig(): TenantConfig {
  return tenantConfig;
}

export function getFeatureFlag(
  select: (config: TenantConfig) => boolean,
): boolean {
  return select(tenantConfig);
}
